/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { Button, Checkbox, Classes, Tag, Tooltip, Text } from '@blueprintjs/core';
import { ColorAliases } from 'styles/variables';
import { FacetCheckbox } from './facetCheckbox';
import {
	hoverRevealChildrenDisplay,
	hoverRevealClassName,
	interactiveListItem,
} from 'styles/styledComponentUtils';
import { SetOfTotal } from '../setOfTotal';
import { Sizes } from 'styles/variables-slykit';
import { HighlightTerms } from '/views/documentViewer';

/**
 * Handles the rendering of a single facet
 * @param {string} facetName = 'n/a',
 * @param {object} facetCountProps props for the <FacetCount/> component,
 * @param {object} facetCheckboxProps props for the <FacetCount/> component,
 * @param {function} onClickSelect, callback for clicking the checkbox
 * @param {function} onClickExclude callback for clicking the "x" to exclude
 * @param {function} onClickOpen callback for clicking the arrow to open
 * @param {boolean} isOpen is the arrow open or closed
 * @param {boolean} isBottom show a dot in place of the arrow
 * @param {number} depth how far to indent hierarchal items. omit/undefined for a standard state
 * @param {boolean} disabled
 */
export const FacetItem = ({
	facetName = 'n/a',

	facetCountProps = {},

	facetCheckboxProps = {},

	facetTextFilter,

	onClickSelect,
	onClickExclude,
	onClickOpen,
	isOpen = false,
	isBottom = false,
	depth,

	disabled = false,
	...props
}) => {
	// STATELESS //

	const toggleSelected = (e) => {
		e.preventDefault();
		if (onClickSelect) onClickSelect(e);
	};

	const toggleExcluded = (e) => {
		e.stopPropagation();
		if (onClickExclude) onClickExclude(e);
	};

	const toggleOpen = (e) => {
		e.stopPropagation();
		if (onClickOpen) onClickOpen(e);
	};

	const { isExcluded, isAncestorExcluded, isAncestorSelected } = facetCheckboxProps;

	return (
		<div
			cy-test="facet-item"
			css={[wrapper, interactiveListItem, hoverRevealChildrenDisplay]}
			onClick={toggleSelected}
			{...props}
		>
			{depth != null && (
				<Button
					icon={isBottom ? 'dot' : isOpen ? 'chevron-down' : 'chevron-right'}
					minimal
					small
					disabled={isBottom}
					onClick={toggleOpen}
					css={css`
						margin-right: ${Sizes.xsm};
						margin-left: ${depth * 24 - 6}px;
						${isBottom &&
						`
							cursor: initial !important;
							opacity: 0.33;
						`}
					`}
				/>
			)}
			<FacetCheckbox {...facetCheckboxProps} />
			<FacetLabel
				name={facetName}
				facetTextFilter={facetTextFilter}
				css={label}
				isExcluded={isExcluded || isAncestorExcluded}
				disabled={disabled || isAncestorExcluded || isAncestorSelected}
			/>
			<SetOfTotal {...facetCountProps} />
			<Tooltip hoverOpenDelay={2000} content={isExcluded ? 'Reset' : 'Exclude'} className={hoverRevealClassName}>
				<Button
					icon={isExcluded ? 'reset' : 'small-cross'}
					onClick={toggleExcluded}
					// minimal
					outlined
					small
					css={css`
						margin-left: ${Sizes.xsm};
					`}
				/>
			</Tooltip>
		</div>
	);
};

/**
 * A placeholder for FacetDetail
 */
export const FacetDetailSkeleton = ({ ...props }) => {
	// STATELESS //
	return (
		<div css={[wrapper, interactiveListItem, hoverRevealChildrenDisplay]} {...props}>
			<Checkbox
				className={Classes.SKELETON}
				css={css`
					margin: 0 ${Sizes.xsm} 0 0;
				`}
			/>
			<span className={Classes.SKELETON}>Loading...</span>
		</div>
	);
};

export const FacetLabel = ({ name, emptyName = '(Unnamed)', isExcluded, disabled, facetTextFilter, ...props }) => {
	// STATELESS //
	const nameText = name || emptyName;
	return (
		<HighlightTerms
			terms={facetTextFilter ? [facetTextFilter] : []}
			wrapperElement={Text}
			title={nameText}
			children={nameText}
			css={[!name && labelEmpty, isExcluded && labelExcluded, disabled && labelDisabled]}
			ellipsize
			{...props}
		/>
	);
};

export const FACET_ITEM_HEIGHT = 32; // px

const wrapper = css`
	display: flex;
	align-items: center;
	padding: 0 ${Sizes.lg};
	height: ${FACET_ITEM_HEIGHT}px;
	overflow: hidden;
`;

const label = css`
	flex: 1 1 auto;
`;

const labelDisabled = css`
	color: ${ColorAliases.PT_TEXT_COLOR_DISABLED};
`;
const labelEmpty = css`
	color: ${ColorAliases.PT_TEXT_COLOR_DISABLED};
	font-weight: bold;
`;
const labelExcluded = css`
	color: ${ColorAliases.PT_TEXT_COLOR_DISABLED};
	text-decoration: line-through;
	font-style: italic;
	padding-right: 3px;
`;

/**
 * This should maybe be a facet item? IDK. It should change somehow. Its confusing
 */
export const ExcludedFacetsDisplay = ({ excludedFacets, toggleFacet, property }) => {
	// STATELESS //

	const excluded = Object.entries(excludedFacets)
		.filter((entry) => entry[0] === property)
		.map((entry) => {
			return Object.entries(entry[1])
				.filter((e) => {
					return e[1] === 'off';
				})
				.map((e) => {
					return { key: entry[0], value: e[0] };
				});
		})
		.flat();

	if (excluded.length === 0) return null;

	return (
		<div
			css={css`
				padding: ${Sizes.xsm} ${Sizes.lg} 0;
				display: flex;
				flex-flow: row wrap;
			`}
		>
			{excluded.map((item) => {
				return (
					<Tag
						key={item.value}
						css={css`
							margin: 0 ${Sizes.sm} ${Sizes.sm} 0;
						`}
						onRemove={() => {
							toggleFacet(item.key, item.value);
						}}
						interactive
						minimal
					>
						<FacetLabel name={item.value} isExcluded={true} />
					</Tag>
				);
			})}
		</div>
	);
};
